import { useState } from 'react';

import { MISSING } from 'common/helpers/format';
import ModalApp from 'components/modalApp';
import Value from 'components/value';

import * as S from './styles';

interface IProps {
  /**
   * The ticket's `attachments`, exactly as the payload sends them. Two shapes
   * in the wild, so this takes either — see `normalize`.
   */
  data?: any[];
}

interface Attachment {
  url: string;
  name: string;
}

const FileIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 3v5h5M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8l-5-5z" />
  </svg>
);

/**
 * The URL and a name for each attachment.
 *
 * The admin detail endpoint resolves the stored file names to full URLs and
 * sends them as plain strings; the app's own endpoints send
 * `{ fileName, fileUrl }` objects out of the same column, which is the shape
 * swagger documents for both. Reading only one of them is what left this
 * section off the screen, so read either — and derive a name from the URL
 * when the string form gives us nothing else to call it.
 */
const normalize = (data?: any[]): Attachment[] =>
  (Array.isArray(data) ? data : []).reduce((list: Attachment[], item: any) => {
    const url = typeof item === 'string' ? item : item?.fileUrl;
    if (!url) return list;

    const fromUrl = decodeURIComponent(url.split('?')[0].split('/').pop() || '');

    return [...list, { url, name: (typeof item === 'string' ? fromUrl : item?.fileName) || fromUrl || 'Attachment' }];
  }, []);

/** One tile. Holds its own failed flag, so one dead URL is one dead tile. */
function AttachmentTile({ item, onOpen }: { item: Attachment; onOpen: () => void }) {
  const [failed, setFailed] = useState(false);

  return (
    <S.Tile type="button" onClick={onOpen} title={item.name}>
      {failed ? (
        <S.Fallback>
          <FileIcon />
        </S.Fallback>
      ) : (
        <S.Image src={item.url} alt={item.name} onError={() => setFailed(true)} />
      )}
      <S.Name>{item.name}</S.Name>
    </S.Tile>
  );
}

/**
 * The attachments a requester filed the ticket with.
 *
 * They are jpeg/png only and capped at 10MB by the upload endpoint, so a
 * thumbnail is the right summary and a click gets the full picture. When the
 * image itself cannot be fetched the preview offers the URL instead, because
 * "the panel cannot render it" and "there is nothing to see" are different
 * answers and only one of them is ours to fix.
 */
export default function TicketDetailAttachments({ data }: IProps) {
  const attachments = normalize(data);
  const [preview, setPreview] = useState<Attachment | null>(null);
  const [previewFailed, setPreviewFailed] = useState(false);

  const handleOpen = (item: Attachment) => {
    setPreviewFailed(false);
    setPreview(item);
  };

  const handleClose = () => setPreview(null);

  if (!attachments.length) return <Value fallback={MISSING.none} />;

  return (
    <>
      <S.List>
        {attachments.map((item, index) => (
          <AttachmentTile key={`${item.url}-${index}`} item={item} onOpen={() => handleOpen(item)} />
        ))}
      </S.List>

      <ModalApp show={!!preview} handleClose={handleClose} fixed center title={preview?.name}>
        {previewFailed ? (
          <S.PreviewEmpty>
            <span>This attachment could not be loaded.</span>
            <a href={preview?.url} target="_blank" rel="noreferrer">
              Open the original
            </a>
          </S.PreviewEmpty>
        ) : (
          <S.Preview src={preview?.url} alt={preview?.name} onError={() => setPreviewFailed(true)} />
        )}
      </ModalApp>
    </>
  );
}
